/**
 * @fileoverview The error a failed `FileReader` read rejects with, plus the
 * helpers that turn it into a Sentry event and into user-facing copy.
 *
 * Deliberately standalone rather than part of `uploadImage.ts`:
 * `instrumentation-client.ts` consumes it in `beforeSend`, and that module is
 * loaded before hydration on every page — it must not drag in the canvas /
 * HEIC-decoder graph.
 */

/**
 * A `FileReader` handed back an error instead of bytes.
 *
 * Exists because `reader.onerror` is called with a `ProgressEvent`, not an
 * `Error`: wiring it straight to a promise's `reject` (what
 * `readImageFileAsDataUrl` used to do) rejects with an event object, which
 * `Sentry.captureException` can only render as "Event `ProgressEvent`
 * (type=error) captured as exception" — an untitled issue with no filename and
 * no reader error code. The real cause lives on `reader.error`, a
 * `DOMException` whose `name` is one of
 * `NotReadableError` / `NotFoundError` / `SecurityError` / `AbortError`.
 *
 * `message` carries only the `DOMException` name so the Sentry title stays low
 * cardinality and groupable; the file's identity rides on the instance and is
 * attached to the event in `instrumentation-client.ts`.
 */
export class FileReadError extends Error {
  /** `reader.error.name` when the browser supplied one, else undefined. */
  readErrorName?: string
  fileName: string
  fileSize: number
  /** The File's MIME type, or `''` when the browser reported none. */
  fileType: string

  constructor(file: File, readError: DOMException | null) {
    super(
      readError
        ? `Could not read image file (${readError.name})`
        : 'Could not read image file (no reader error reported)',
    )
    this.name = 'FileReadError'
    this.readErrorName = readError?.name
    this.fileName = file.name
    this.fileSize = file.size
    this.fileType = file.type
    if (readError) this.cause = readError
  }
}

export function isFileReadError(error: unknown): error is FileReadError {
  return error instanceof FileReadError
}

/**
 * The diagnostic payload a Sentry event needs to explain a read failure without
 * a session replay. Kept off `message` so it can't fragment issue grouping.
 */
export function fileReadErrorContext(error: FileReadError): Record<string, unknown> {
  return {
    file_name: error.fileName,
    file_size: error.fileSize,
    file_type: error.fileType || '(none)',
    read_error: error.readErrorName ?? '(none)',
  }
}

/**
 * Group every read failure with the same cause into ONE Sentry issue.
 *
 * Keyed on the `DOMException` name rather than the stack, because the six
 * upload surfaces that share `readImageFileAsDataUrl` would otherwise mint a
 * group each for what is one browser-level failure mode.
 */
export function fileReadErrorFingerprint(error: FileReadError): string[] {
  return ['file-read-error', error.readErrorName ?? 'unknown']
}

/**
 * User-facing explanation for a read failure, or `null` when the cause is
 * unknown (or not one we can turn into advice) and the caller should fall back
 * to its generic "try a different file" copy.
 */
export function fileReadFailureReason(error: unknown): string | null {
  if (!isFileReadError(error)) return null
  switch (error.readErrorName) {
    case 'NotFoundError':
      return 'the file is no longer where it was picked from'
    case 'NotReadableError':
      return 'it may have moved, or its drive disconnected'
    case 'SecurityError':
      return 'the browser blocked access to it'
    default:
      return null
  }
}

/**
 * Human-readable summary of a failed read, naming the file so the user knows
 * which one to retry. Falls back to the file name alone when the browser gave
 * us no usable cause.
 */
export function describeFileReadFailure(fileName: string, error: unknown): string {
  const reason = fileReadFailureReason(error)
  return reason ? `"${fileName}" could not be read — ${reason}.` : `"${fileName}" could not be read.`
}

/** How many failing files an error zone names before falling back to a count. */
const MAX_NAMED_READ_FAILURES = 3

/**
 * Collapse per-file failure sentences (from `describeFileReadFailure`) into the
 * one line an error zone shows. Names the first few files — a read failure is
 * per-file and usually fixable, so the user needs to know which one to act on —
 * and counts the rest so a 20-file drop can't flood the zone.
 */
export function summarizeFileReadFailures(failures: string[]): string {
  const named = failures.slice(0, MAX_NAMED_READ_FAILURES)
  const remaining = failures.length - named.length
  const sentences = [...named]
  if (remaining > 0) sentences.push(`${remaining} more could not be read.`)
  else sentences.push('Try a different file.')
  return sentences.join(' ')
}
